<template>
  <div class="hq-admin">
    <h1>HQ Images — Admin</h1>

    <!-- 20 GB available -->
    <div v-if="msg" :class="['notice', msgType]">{{ msg }}</div>

    <!-- Cards -->
    <section class="cards">
      <div class="card">
        <div class="label">Total Images</div>
        <div class="value">{{ stats.total_images.toLocaleString() }}</div>
      </div>
      <div class="card">
        <div class="label">Total Storage</div>
        <div class="value">{{ prettyBytes(stats.total_bytes) }}</div>
      </div>
      <div class="card">
        <div class="label">Ready</div>
        <div class="value">
          {{ (stats.by_status.ready?.count || 0).toLocaleString() }}
          <small>({{ prettyBytes(stats.by_status.ready?.bytes || 0) }})</small>
        </div>
      </div>
      <div class="card">
        <div class="label">Largest (ready)</div>
        <div class="value">{{ prettyBytes(stats.largest_bytes_ready || 0) }}</div>
      </div>
      <div class="card">
        <div class="label">Disk Used</div>
        <div class="value">
          <template v-if="stats.disk.used_pct != null">
            {{ stats.disk.used_pct }}% <small>({{ prettyBytes(stats.disk.used) }} / {{ prettyBytes(stats.disk.total) }})</small>
          </template>
          <template v-else>n/a</template>
        </div>
      </div>
    </section>

    <!-- Filters -->
    <section class="filters card">
      <div class="row">
        <label>Status:
          <select v-model="status">
            <option value="">All</option>
            <option value="ready">ready</option>
            <option value="uploading">uploading</option>
            <option value="failed">failed</option>
          </select>
        </label>
        <label>Search:
          <input v-model="q" placeholder="username or request id"/>
        </label>
        <button @click="loadList(1)">Apply</button>
        <button class="ghost" @click="resetFilters">Reset</button>
      </div>
    </section>

    <!-- Top owners -->
    <section class="card" v-if="owners.length">
      <h3>Top Owners by Storage</h3>
      <table class="table">
        <thead><tr><th>User</th><th>Images</th><th>Bytes</th></tr></thead>
        <tbody>
          <tr v-for="o in owners" :key="o.owner_username">
            <td>{{ o.owner_username }}</td>
            <td>{{ Number(o.count).toLocaleString() }}</td>
            <td>{{ prettyBytes(Number(o.bytes)) }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- List -->
    <section class="card">
      <h3>Latest Images</h3>
      <table class="table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Request</th>
            <th>User</th>
            <th>Status</th>
            <th>MIME</th>
            <th>Bytes</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="it in items" :key="it.id">
            <td>{{ it.id }}</td>
            <td>
              <router-link :to="{ name: 'ViewImage', params: { requestID: it.request_id }}">
                #{{ it.request_id }}
              </router-link>
            </td>
            <td>{{ it.owner_username }}</td>
            <td>{{ it.status }}</td>
            <td>{{ it.mime }}</td>
            <td>{{ prettyBytes(it.byte_size) }}</td>
            <td>{{ fmt(it.created_at) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="pagination">
        <button @click="go(page-1)" :disabled="page<=1">Prev</button>
        Page {{ page }} / {{ totalPages }}
        <button @click="go(page+1)" :disabled="page>=totalPages">Next</button>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminHQImages',
  data() {
    return {
      msg: '', msgType: 'info',
      stats: { total_images:0, total_bytes:0, by_status:{}, by_mime:{}, disk:{} },
      owners: [],
      // list
      items: [],
      page: 1,
      limit: 25,
      total: 0,
      // filters
      status: '',
      q: ''
    };
  },
  computed: {
    totalPages() { return Math.max(1, Math.ceil(this.total / this.limit)); }
  },
  async mounted() {
    await Promise.all([ this.loadStats(), this.loadOwners(), this.loadList(1) ]);
  },
  methods: {
    tokenHdr() {
      const t = localStorage.getItem('token');
      return { Authorization: `Bearer ${t}` };
    },
    async loadStats() {
      try {
        const res = await axios.get('/admin/hq_images/stats.php', { headers: this.tokenHdr() });
        if (res.data?.status === 'ok') this.stats = res.data;
        else this.setMsg('Failed to load stats','error');
      } catch (e) { this.setMsg('Failed to load stats','error'); }
    },
    async loadOwners() {
      try {
        const res = await axios.get('/admin/hq_images/top_owners.php?limit=10', { headers: this.tokenHdr() });
        if (res.data?.status === 'ok') this.owners = res.data.owners || [];
      } catch {
        console.log("Loaded");
      }
    },
    async loadList(page) {
      if (page) this.page = page;
      const p = new URLSearchParams();
      p.set('page', this.page);
      p.set('limit', this.limit);
      if (this.status) p.set('status', this.status);
      if (this.q) p.set('q', this.q);
      try {
        const res = await axios.get(`/admin/hq_images/list.php?${p.toString()}`, { headers: this.tokenHdr() });
        if (res.data?.status === 'ok') {
          this.items = res.data.items || [];
          this.total = res.data.total || 0;
        } else this.setMsg('Failed to load list','error');
      } catch (e) { this.setMsg('Failed to load list','error'); }
    },
    go(p) { if (p>=1 && p<=this.totalPages) this.loadList(p); },
    resetFilters() { this.status=''; this.q=''; this.loadList(1); },
    fmt(s) { return s ? new Date(s.replace(' ','T')+'Z').toLocaleString() : '—'; },
    prettyBytes(bytes) {
      const b = Number(bytes)||0;
      if (b < 1024) return `${b} B`;
      if (b < 1024**2) return `${(b/1024).toFixed(1)} KB`;
      if (b < 1024**3) return `${(b/1024**2).toFixed(2)} MB`;
      return `${(b/1024**3).toFixed(2)} GB`;
    },
    setMsg(m,t='info'){ this.msg = m; this.msgType = t; setTimeout(()=>{ this.msg=''; }, 4000); },
  }
};
</script>

<style scoped>
.hq-admin { max-width: 1100px; margin: 0 auto; padding: 1rem; }
.notice { margin: .5rem 0; padding: .6rem .8rem; border-radius: 8px; font-weight:600; }
.notice.info { background:#e0f2fe; color:#0c4a6e; border:1px solid #bae6fd; }
.notice.error{ background:#fee2e2; color:#7f1d1d; border:1px solid #fecaca; }

.cards { display:grid; grid-template-columns: repeat(auto-fill, minmax(180px,1fr)); gap:.6rem; margin:.6rem 0 1rem; }
.card { background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding:.75rem; box-shadow:0 6px 16px rgba(16,24,40,.06); }
.card .label { color:#6b7280; font-weight:700; font-size:.85rem; }
.card .value { font-size:1.2rem; font-weight:800; color:#0f2740; }

.filters .row { display:flex; flex-wrap:wrap; gap:.5rem; align-items:center; }
.filters input, .filters select { padding:.35rem .5rem; border:1px solid #d1d5db; border-radius:8px; }
.filters button { padding:.4rem .7rem; border-radius:8px; border:1px solid #cbd5e1; background:#f9fafb; }
.filters .ghost { background:#fff; }

.table { width:100%; border-collapse:collapse; font-size:13px; }
.table th, .table td { border:1px solid #e5e7eb; padding:.45rem .5rem; }
.pagination { display:flex; justify-content:center; align-items:center; gap:.5rem; padding:.5rem 0; }
.pagination button { padding:.35rem .6rem; border:1px solid #cbd5e1; border-radius:8px; background:#fff; }
</style>
